import { Link, type To } from 'react-router';

type ScopeAction = Readonly<{
  label: string;
  ariaLabel?: string;
}> & (
  | Readonly<{ kind: 'link'; to: To; onClick?: never }>
  | Readonly<{ kind: 'button'; onClick: () => void; to?: never }>
);

type Scope =
  | Readonly<{ kind: 'portfolio'; name?: never; entityType?: never; unavailableLabel?: never }>
  | Readonly<{ kind: 'building'; name: string; entityType?: never; unavailableLabel?: never }>
  | Readonly<{ kind: 'entity'; name: string; entityType: string; unavailableLabel?: never }>
  | Readonly<{ kind: 'unavailable'; name?: never; entityType?: never; unavailableLabel?: string }>;

export type ScopeIndicatorProps = Scope & Readonly<{
  action?: ScopeAction;
}>;

const uuidPattern = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
const opaqueCrockfordTokenPattern = /^(?=.*[0-9])(?=.*[A-Z])[0-9A-HJKMNP-TV-Z]{20,32}$/i;
const longHexTokenPattern = /^(?:[a-z]+[_-])?[0-9a-f]{16,}$/i;
const prefixedOpaqueTokenPattern = /^(?:[a-z][a-z0-9]*[_-])+(?=[0-9a-z]{16,32}$)(?=[0-9a-z]*[0-9])[0-9a-z]+$/i;
const lowercaseSlugPattern = /^[a-z0-9]+(?:[-_][a-z0-9]+){2,}$/;
const domainIdentifierPattern = /^(?:building|development|property|entity|organisation|organization|org|client|portfolio)[_-][a-z0-9]+(?:[-_][a-z0-9]+)*$/;
const technicalPathPattern = /^(?:\/|urn:|[a-z][a-z0-9+.-]*:\/\/)/i;

function isHumanName(value: string) {
  const trimmed = value.trim();
  return trimmed.length > 0
    && !uuidPattern.test(trimmed)
    && !opaqueCrockfordTokenPattern.test(trimmed)
    && !longHexTokenPattern.test(trimmed)
    && !prefixedOpaqueTokenPattern.test(trimmed)
    && !lowercaseSlugPattern.test(trimmed)
    && !domainIdentifierPattern.test(trimmed)
    && !technicalPathPattern.test(trimmed);
}

function resolvedScope(scope: ScopeIndicatorProps) {
  if (scope.kind === 'portfolio') return { kind: scope.kind, label: 'Portfolio scope', name: 'All buildings' };
  if (scope.kind === 'unavailable') {
    const unavailableLabel = scope.unavailableLabel?.trim();
    return {
      kind: scope.kind,
      label: 'Current scope',
      name: unavailableLabel && isHumanName(unavailableLabel) ? unavailableLabel : 'Unavailable',
    };
  }
  if (!isHumanName(scope.name) || (scope.kind === 'entity' && !isHumanName(scope.entityType))) {
    return { kind: 'unavailable' as const, label: 'Current scope', name: 'Unavailable' };
  }
  return scope.kind === 'building'
    ? { kind: scope.kind, label: 'Building scope', name: scope.name.trim() }
    : { kind: scope.kind, label: `${scope.entityType.trim()} scope`, name: scope.name.trim() };
}

export function ScopeIndicator(scope: ScopeIndicatorProps) {
  const resolved = resolvedScope(scope);
  const action = scope.action;
  return <div
    className={`scope-indicator scope-indicator--${resolved.kind}`}
    role="group"
    aria-label={`${resolved.label}: ${resolved.name}`}
    data-scope-kind={resolved.kind}
  >
    <span className="scope-indicator__label">{resolved.label}</span>
    <strong className="scope-indicator__name">{resolved.name}</strong>
    {action && (action.kind === 'link'
      ? <Link className="scope-indicator__action" to={action.to} aria-label={action.ariaLabel}>{action.label}</Link>
      : <button className="scope-indicator__action" type="button" onClick={action.onClick} aria-label={action.ariaLabel}>{action.label}</button>)}
  </div>;
}
